import * as locales from "./locales";
import { I18n, getLocale } from "@devforce/trailhead-client-locale";
const i18n = new I18n({ helpers: locales });

function isYesterday(date: Date, today: Date) {
  const differenceMS = today.getTime() - date.getTime();
  const differenceDays = differenceMS / (1000 * 3600 * 24);
  return differenceDays > 1 && differenceDays < 2;
}

export function timestamp(createdAt: string) {
  const date = new Date(createdAt);
  const today = new Date();

  const isToday = date.toDateString() === today.toDateString();
  const isThisYear = date.getFullYear() === today.getFullYear();
  const yesterday = isYesterday(date, today);

  const timeFormatter = new Intl.DateTimeFormat(getLocale(), {
    month: isToday || yesterday ? undefined : "short",
    day: isToday || yesterday ? undefined : "numeric",
    year: isToday || yesterday || isThisYear ? undefined : "numeric",
    hour: "numeric",
    minute: "numeric",
  });
  const dateTime = timeFormatter.format(date);

  if (isToday) {
    return i18n.t("helpers", "todayTimestamp", { time: dateTime });
  } else if (yesterday) {
    return i18n.t("helpers", "yesterdayTimestamp", { time: dateTime });
  }

  return dateTime;
}

export function lastActivity(isoString?: string | null) {
  if (isoString) {
    const formatter = new Intl.DateTimeFormat(getLocale(), {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const date = new Date(isoString);
    const formatted = formatter.format(date);
    return i18n.t("helpers", "lastActivity", { date: formatted });
  }

  return "";
}

export function isBefore(date: Date, dateToCompare: Date): boolean {
  return date < dateToCompare;
}

export function formatDuration(seconds: number) {
  // Calculate hours, minutes, and remaining seconds
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  // Format the remaining seconds to always have two digits
  const formattedSeconds = remainingSeconds.toString().padStart(2, "0");

  // Determine the correct label for hours and minutes
  if (hours > 0) {
    const hourLabelKey = hours === 1 ? "durationHour" : "durationHours";
    const formattedMinutes = minutes.toString().padStart(2, "0");
    return i18n.t("helpers", hourLabelKey, {
      duration: `${hours}:${formattedMinutes}:${formattedSeconds}`,
    });
  } else {
    const minuteLabelKey = minutes === 1 ? "durationMinute" : "durationMinutes";
    return i18n.t("helpers", minuteLabelKey, {
      duration: `${minutes}:${formattedSeconds}`,
    });
  }
}

export function isOverdue(dueDate: string): boolean {
  if (!dueDate) return false;

  const [year, month, day] = dueDate.split("-").map(Number);

  const dueDateObj = new Date(year, month - 1, day);
  const today = new Date();

  today.setHours(0, 0, 0, 0);
  dueDateObj.setHours(0, 0, 0, 0);

  return today > dueDateObj;
}

export function formatDueDate(dueDate: string): string {
  if (!dueDate) return "";

  const dateOnly = dueDate.split("T")[0];
  const [year, month, day] = dateOnly.split("-").map(Number);

  const dueDateObj = new Date(year, month - 1, day);
  const formatter = new Intl.DateTimeFormat(getLocale(), {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
  return formatter.format(dueDateObj);
}
